import { create } from 'zustand';
import type { Compound, Offering, CreateOfferingData, UpdateOfferingData } from '../types';
import { offeringService } from '../services/offering.service';

interface OfferingState {
  compounds: Compound[];
  offerings: Offering[];
  isLoadingCompounds: boolean;
  isLoadingOfferings: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;

  // Actions
  fetchCompounds: () => Promise<void>;
  fetchOfferings: () => Promise<void>;
  createOffering: (data: CreateOfferingData) => Promise<Offering>;
  updateOffering: (id: string, data: UpdateOfferingData) => Promise<void>;
  toggleActive: (id: string) => Promise<void>;
  deleteOffering: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useOfferingStore = create<OfferingState>((set, get) => ({
  compounds: [],
  offerings: [],
  isLoadingCompounds: false,
  isLoadingOfferings: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,

  fetchCompounds: async () => {
    set({ isLoadingCompounds: true, error: null });
    try {
      const compounds = await offeringService.getCompounds();
      set({ compounds, isLoadingCompounds: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch compounds';
      set({ error: message, isLoadingCompounds: false });
    }
  },

  fetchOfferings: async () => {
    set({ isLoadingOfferings: true, error: null });
    try {
      const offerings = await offeringService.getOfferings();
      set({ offerings, isLoadingOfferings: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch offerings';
      set({ error: message, isLoadingOfferings: false });
    }
  },

  createOffering: async (data: CreateOfferingData) => {
    set({ isCreating: true, error: null });
    try {
      const newOffering = await offeringService.createOffering(data);
      set((state) => ({
        offerings: [...state.offerings, newOffering],
        isCreating: false,
      }));
      return newOffering;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create offering';
      set({ error: message, isCreating: false });
      throw error;
    }
  },

  updateOffering: async (id: string, data: UpdateOfferingData) => {
    set({ isUpdating: true, error: null });
    try {
      const updatedOffering = await offeringService.updateOffering(id, data);
      set((state) => ({
        offerings: state.offerings.map((o) => (o.id === id ? updatedOffering : o)),
        isUpdating: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update offering';
      set({ error: message, isUpdating: false });
      throw error;
    }
  },

  toggleActive: async (id: string) => {
    const offering = get().offerings.find(o => o.id === id);
    if (!offering) return;
    
    set({ isUpdating: true, error: null });
    try {
      const updatedOffering = await offeringService.toggleActive(id, !offering.isActive);
      set((state) => ({
        offerings: state.offerings.map((o) => (o.id === id ? updatedOffering : o)),
        isUpdating: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to toggle offering status';
      set({ error: message, isUpdating: false });
      throw error;
    }
  },

  deleteOffering: async (id: string) => {
    set({ isDeleting: true, error: null });
    try {
      await offeringService.deleteOffering(id);
      set((state) => ({
        offerings: state.offerings.filter((o) => o.id !== id),
        isDeleting: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete offering';
      set({ error: message, isDeleting: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
